/**
 * ModifierGroupSelector - Global modifier groups (Milk Type, Sugar Level, etc.)
 * Radio-style selection where only one option per group can be selected
 */
import { memo } from 'react';
import { Check } from 'lucide-react';

const ModifierGroupSelector = memo(function ModifierGroupSelector({ 
  modifierGroups = [], 
  selectedModifiers = [], 
  onSelectOption,
  onDeselectOption,
  stepNumber = 2 
}) {
  if (modifierGroups.length === 0) return null;

  // Get selected option for a group
  const getSelectedOption = (groupName) => {
    return selectedModifiers.find(m => m.category === groupName);
  };

  return (
    <div className="space-y-4">
      <h4 className="font-medium text-base flex items-center gap-2">
        <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-xs text-primary font-bold">
          {stepNumber}
        </span>
        Customize Your Order
      </h4>
      
      {modifierGroups.map(group => {
        const selectedOption = getSelectedOption(group.name);
        
        return (
          <div key={group.id || group.name} className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">
                {group.name}
              </span>
              {group.is_required && (
                <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-2 py-0.5 rounded-full">
                  Required
                </span>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              {(group.options || []).map(option => {
                const optionName = option.name;
                // Read price from either price or price_adjustment field
                const optionPrice = option.price ?? option.price_adjustment ?? 0;
                const isSelected = selectedOption?.name === optionName;
                const isDefault = optionName.toLowerCase().includes('regular') || 
                                  optionName.toLowerCase().includes('full cream') ||
                                  optionName.toLowerCase().includes('whole') ||
                                  optionName.toLowerCase().includes('normal') ||
                                  optionPrice === 0;
                
                return (
                  <button
                    key={optionName}
                    onClick={() => {
                      if (isSelected) {
                        // Deselect (unless required)
                        if (!group.is_required) {
                          onDeselectOption(group.name);
                        }
                      } else {
                        onSelectOption(group.name, optionName, optionPrice);
                      }
                    }}
                    data-testid={`modifier-group-${group.name.toLowerCase().replace(/\s+/g, '-')}-${optionName.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/10'
                        : 'border-border bg-card hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        {optionName}
                        {optionPrice > 0 && (
                          <span className="text-primary ml-1 text-xs font-semibold">+${optionPrice.toFixed(2)}</span>
                        )}
                      </span>
                      <div className="flex items-center gap-1">
                        {isDefault && !isSelected && (
                          <span className="text-[10px] text-muted-foreground">FREE</span>
                        )}
                        {isSelected && <Check className="w-4 h-4 text-primary" />}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
});

export default ModifierGroupSelector;
